import {
  esquemaCrearUsuario,
  esquemaDatosUsuario,
  FRECUENCIA_DIARIA,
  type Frecuencia,
  NOMBRE_ROL,
  ROLES_GLOBALES,
  type RolGlobal,
  type RolProceso,
} from "@gestiondoc/shared";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTRPC } from "../api.ts";
import type { ListaDeUsuarios, UsuarioAdministrado } from "../tipos.ts";
import { mensajeDe } from "../utiles.ts";
import { SelectorFrecuencia } from "./Frecuencia.tsx";
import { Modal } from "./ModalFirma.tsx";
import { Aviso, Boton, Campo, CLASE_ENTRADA } from "./ui.tsx";

/**
 * La ficha de una persona, para darla de alta o editarla: nombre, correo, puesto (del listado
 * aprobado DIR-ESP-004), rol, acceso desde fuera, sus procesos y sus diligencias. Todo se guarda
 * en la misma transacción.
 */

const AYUDA_ROL: Record<RolGlobal, string> = {
  colaborador: "Ve y trabaja solo en sus procesos.",
  gerencia: "Ve nivel 3 de todos los procesos y los reportes de auditoría.",
  administrador: "Administra usuarios, catálogos, alertas, formatos y el nivel 4.",
  soporte: "Carga las imágenes de firma y ve la IP de quien firmó.",
};

export type FormularioDeUsuario =
  | { modo: "crear" }
  | { modo: "editar"; usuario: UsuarioAdministrado };

export function FormularioUsuario({
  formulario,
  procesos,
  puestos,
  formatos,
  onCerrar,
  onCreado,
  onGuardado,
}: {
  formulario: FormularioDeUsuario;
  procesos: ListaDeUsuarios["procesos"];
  puestos: ListaDeUsuarios["puestos"];
  formatos: ListaDeUsuarios["formatos"];
  onCerrar: () => void;
  onCreado: (nombre: string, contrasena: string) => void;
  onGuardado: () => void;
}) {
  const trpc = useTRPC();
  const editando = formulario.modo === "editar" ? formulario.usuario : null;
  const [nombre, setNombre] = useState(editando?.nombre ?? "");
  const [correo, setCorreo] = useState(editando?.correo ?? "");
  // El puesto se elige del listado; quien ya tenía uno escrito a mano (antes del listado) lo
  // conserva hasta que se le elija uno.
  const [puestoId, setPuestoId] = useState(editando?.puestoId ?? "");
  const puestoEscrito = editando?.puestoId ? "" : (editando?.puesto ?? "");
  const puesto = puestos.find((p) => p.id === puestoId)?.nombre ?? puestoEscrito;
  const [rolGlobal, setRolGlobal] = useState<RolGlobal>(editando?.rolGlobal ?? "colaborador");
  const [accesoExterno, setAccesoExterno] = useState(editando?.accesoExterno ?? false);
  const [cargaSinFirmas, setCargaSinFirmas] = useState(editando?.cargaSinFirmas ?? false);
  const [accesos, setAccesos] = useState<Record<string, RolProceso | "">>(() => {
    const inicial: Record<string, RolProceso | ""> = {};
    for (const p of editando?.procesos ?? []) inicial[p.procesoId] = p.rol;
    return inicial;
  });
  const [diligencias, setDiligencias] = useState<DiligenciaElegida[]>(() =>
    (editando?.diligencias ?? []).map((d) => ({ ...d })),
  );
  const [error, setError] = useState<string | null>(null);

  const crear = useMutation(
    trpc.usuarios.crear.mutationOptions({
      onSuccess: (r) => onCreado(nombre.trim(), r.contrasenaTemporal),
      onError: (e) => setError(mensajeDe(e)),
    }),
  );
  const actualizar = useMutation(
    trpc.usuarios.actualizar.mutationOptions({
      onSuccess: onGuardado,
      onError: (e) => setError(mensajeDe(e)),
    }),
  );
  const enviando = crear.isPending || actualizar.isPending;

  function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const listaDeProcesos = Object.entries(accesos)
      .filter((par): par is [string, RolProceso] => par[1] !== "")
      .map(([procesoId, rol]) => ({ procesoId, rol }));
    if (!puesto) {
      setError("Elige el puesto del listado");
      return;
    }
    const base = {
      nombre,
      puestoId: puestoId || null,
      puesto,
      rolGlobal,
      accesoExterno,
      ...(editando ? { cargaSinFirmas } : {}),
      procesos: listaDeProcesos,
      diligencias,
    };
    if (editando) {
      const datos = esquemaDatosUsuario.safeParse(base);
      if (!datos.success) {
        setError(datos.error.issues[0]?.message ?? "Revisa los datos");
        return;
      }
      actualizar.mutate({ id: editando.id, ...datos.data });
    } else {
      const datos = esquemaCrearUsuario.safeParse({ ...base, correo });
      if (!datos.success) {
        setError(datos.error.issues[0]?.message ?? "Revisa los datos");
        return;
      }
      crear.mutate(datos.data);
    }
  }

  return (
    <Modal
      titulo={editando ? `Editar a ${editando.nombre}` : "Nuevo usuario"}
      onCerrar={onCerrar}
      bloqueado={enviando}
    >
      <form onSubmit={enviar} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo etiqueta="Nombre completo" ayuda="Como va a salir en las firmas.">
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className={CLASE_ENTRADA}
              autoComplete="off"
              // biome-ignore lint/a11y/noAutofocus: el formulario se abre para escribir aquí
              autoFocus
            />
          </Campo>
          <Campo
            etiqueta="Correo"
            ayuda={
              editando
                ? "No se cambia: Google entra por este correo."
                : "El de SMG: con ese mismo entra con Google."
            }
          >
            <input
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              className={CLASE_ENTRADA}
              disabled={!!editando}
              autoComplete="off"
            />
          </Campo>
          <Campo
            etiqueta="Puesto"
            ayuda={
              puestoEscrito && !puestoId
                ? "Escrito antes del listado aprobado: elige el que le corresponde."
                : "Del listado aprobado (DIR-ESP-004). Sale en las firmas y los sellos."
            }
          >
            <select
              value={puestoId}
              onChange={(e) => setPuestoId(e.target.value)}
              className={CLASE_ENTRADA}
            >
              <option value="">
                {puestoEscrito ? `${puestoEscrito} (fuera del listado)` : "Elige un puesto"}
              </option>
              {puestos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.codigo} · {p.nombre}
                </option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta="Rol" ayuda={AYUDA_ROL[rolGlobal]}>
            <select
              value={rolGlobal}
              onChange={(e) => setRolGlobal(e.target.value as RolGlobal)}
              className={CLASE_ENTRADA}
            >
              {ROLES_GLOBALES.map((r) => (
                <option key={r} value={r}>
                  {NOMBRE_ROL[r]}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <label className="flex items-start gap-2 text-[12.5px] text-texto-2">
          <input
            type="checkbox"
            checked={accesoExterno}
            onChange={(e) => setAccesoExterno(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            <span className="font-semibold">Puede entrar desde fuera de la oficina.</span> Desde
            fuera se le pide además el código de una aplicación de autenticación en su celular, y no
            ve los documentos de nivel 4.
          </span>
        </label>

        {editando && (
          <label className="flex items-start gap-2 text-[12.5px] text-texto-2">
            <input
              type="checkbox"
              checked={cargaSinFirmas}
              onChange={(e) => setCargaSinFirmas(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="font-semibold">Sube documentos sin firmas (temporal).</span> Lo que
              suba como archivo queda vigente de una vez, sin ruta de aprobación. Es para cargar
              documentos que ya existen: apágalo al terminar la carga.
            </span>
          </label>
        )}

        <fieldset>
          <legend className="mb-1 block text-[11.5px] font-semibold text-texto-2">Procesos</legend>
          <p className="mb-2 text-[11.5px] text-texto-3">
            Carga: crea y modifica documentos del proceso. Consulta: solo los ve. Sin acceso: no
            aparece en su menú.
          </p>
          {procesos.length === 0 ? (
            <p className="text-[12.5px] text-texto-3">No hay procesos activos.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {procesos.map((p) => (
                <label
                  key={p.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-borde px-3 py-2 text-[12.5px]"
                >
                  <span className="min-w-0">
                    <span className="font-display font-semibold tracking-wide">{p.codigo}</span>
                    <span className="ml-2 truncate text-texto-2">{p.nombre}</span>
                  </span>
                  <select
                    value={accesos[p.id] ?? ""}
                    onChange={(e) =>
                      setAccesos((a) => ({ ...a, [p.id]: e.target.value as RolProceso | "" }))
                    }
                    className="rounded-md border border-borde bg-fondo px-2 py-1 text-[12px] outline-none focus:border-marino"
                  >
                    <option value="">Sin acceso</option>
                    <option value="consulta">Consulta</option>
                    <option value="carga">Carga</option>
                  </select>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <DiligenciasDeLaPersona
          formatos={formatos}
          elegidas={diligencias}
          onCambio={setDiligencias}
        />

        {error && <Aviso>{error}</Aviso>}

        <div className="flex justify-end gap-2 pt-2">
          <Boton variante="secundario" onClick={onCerrar} disabled={enviando}>
            Cancelar
          </Boton>
          <Boton type="submit" disabled={enviando}>
            {enviando ? "Guardando…" : editando ? "Guardar cambios" : "Crear usuario"}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

type DiligenciaElegida = { documentoId: string; frecuencia: Frecuencia };

/** Cuántos formatos ofrece el buscador a la vez. */
const MAX_SUGERENCIAS = 8;

/**
 * Las diligencias de la persona dentro de su ficha: un buscador por código o nombre entre los
 * formatos de registro vigentes, y la lista de los elegidos con cada cuánto le toca cada uno.
 */
function DiligenciasDeLaPersona({
  formatos,
  elegidas,
  onCambio,
}: {
  formatos: ListaDeUsuarios["formatos"];
  elegidas: DiligenciaElegida[];
  onCambio: (d: DiligenciaElegida[]) => void;
}) {
  const [busqueda, setBusqueda] = useState("");
  const formatoDe = (id: string) => formatos.find((f) => f.id === id);
  const yaElegidos = new Set(elegidas.map((d) => d.documentoId));
  const texto = busqueda.trim().toLowerCase();
  const sugerencias =
    texto === ""
      ? []
      : formatos
          .filter(
            (f) =>
              f.vigente &&
              !yaElegidos.has(f.id) &&
              (f.codigo.toLowerCase().includes(texto) || f.nombre.toLowerCase().includes(texto)),
          )
          .slice(0, MAX_SUGERENCIAS);

  function agregar(documentoId: string) {
    onCambio([...elegidas, { documentoId, frecuencia: FRECUENCIA_DIARIA }]);
    setBusqueda("");
  }
  function cambiar(documentoId: string, frecuencia: Frecuencia) {
    onCambio(
      elegidas.map((d) => (d.documentoId === documentoId ? { documentoId, frecuencia } : d)),
    );
  }
  function quitar(documentoId: string) {
    onCambio(elegidas.filter((d) => d.documentoId !== documentoId));
  }

  return (
    <fieldset>
      <legend className="mb-1 block text-[11.5px] font-semibold text-texto-2">Diligencias</legend>
      <p className="mb-2 text-[11.5px] text-texto-3">
        Los formatos de registro que esta persona llena, y cada cuánto. Le aparecen en su pantalla
        Diligencias los días que le tocan, aunque no cargue en el proceso del formato.
      </p>
      {elegidas.length > 0 && (
        <ul className="mb-2 divide-y divide-borde/60 rounded-md border border-borde">
          {elegidas.map((d) => {
            const f = formatoDe(d.documentoId);
            return (
              <li key={d.documentoId} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <span className="font-mono text-[12px] text-marino">{f?.codigo ?? "?"}</span>
                <span className="min-w-0 flex-1 truncate text-[12.5px]">
                  {f?.nombre ?? "Formato que ya no está"}
                  {f && !f.vigente && (
                    <span className="ml-1.5 text-[11px] text-texto-3">(sin revisión vigente)</span>
                  )}
                </span>
                <SelectorFrecuencia
                  valor={d.frecuencia}
                  onChange={(fr) => cambiar(d.documentoId, fr)}
                  etiqueta={`Cada cuánto llena ${f?.codigo ?? "el formato"}`}
                />
                <button
                  type="button"
                  aria-label={`Quitar ${f?.codigo ?? "el formato"}`}
                  title="Quitar"
                  onClick={() => quitar(d.documentoId)}
                  className="grid size-7 place-items-center rounded-full text-texto-3 hover:bg-rojo-fondo hover:text-rojo"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <input
        type="search"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar un formato por código o nombre para agregarlo"
        aria-label="Buscar formato"
        className={CLASE_ENTRADA}
      />
      {texto !== "" && (
        <ul className="mt-1 max-h-[200px] overflow-y-auto rounded-md border border-borde bg-white text-[12.5px]">
          {sugerencias.length === 0 ? (
            <li className="px-3 py-2 text-texto-3">Ningún formato de registro vigente coincide.</li>
          ) : (
            sugerencias.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => agregar(f.id)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-fondo"
                >
                  <span className="font-mono text-[12px] text-marino">{f.codigo}</span>
                  <span className="min-w-0 flex-1 truncate">{f.nombre}</span>
                  <span className="text-[11.5px] text-texto-3">{f.proceso}</span>
                  <span className="text-[12px] font-semibold text-marino">+ Agregar</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </fieldset>
  );
}
